// React core imports
import { FormEvent, Fragment, ReactElement, useEffect, useMemo } from "react";
// Styles
import styles from "./OfferList.module.css";
// Types
import { Site } from "@/types/sites";
import { AdUnitResponse } from "@/types/adUnit";
import {
    ClickoutLocation,
    GA4Offer,
    OfferPageTemplateResponse,
    OfferPresentation,
    PageFeature,
    SanitisedOffer,
} from "src/types/offers";
// Constants
import { CLICK_OUT_COOKIE_NAME } from "../../../constants";
// Components
import AdUnit, { adRowClass } from "src/components/OfferPage/AdUnit";
import { OfferBlockWithForm } from "@/components/OfferPage/OfferBlockWithForm";
import TitleUnit from "@/components/OfferPage/TitleUnit";
// Hooks
import { useBreakpoint } from "../../../hooks/useBreakpoint";
// Utils
import { buttonToggle } from "src/utils/utils";
import { sendBeaconOrFetch } from "../../../utils/apiUtils";
import { getMerchantOrderingFromOfferPage } from "../../../utils/offerPageDataUtils";
import {
    getClientCookie,
    getFacebookCookies,
    setCookie,
} from "src/utils/cookies/client";
import { getGlobalConfig } from "src/api/globalConfig";
import { isPrefetch } from "@/lib/prefetch";

export const offerRowClass = "offerRowHover";

const logNewGoogleAnalyticsEvent = (
    eventName: string,
    offerLink?: string,
    offerDetails?: GA4Offer,
    epcId?: string
): void => {
    if (isPrefetch()) {
        return;
    }

    const ecommerceData = formatOfferDetailsForGA(offerDetails);
    window.dataLayer?.push({
        event: eventName,
        offerLink: offerLink,
        offerPage: window.location.href,
        ecommerce: ecommerceData,
        epcId: epcId,
    });
};

const logNewOfferListViewEvent = (offerDetails: GA4Offer) => {
    if (isPrefetch()) {
        return;
    }

    const ecommerceData = formatOfferDetailsForGA(offerDetails);

    window.dataLayer?.push({
        event: "view_item_list",
        ecommerce: ecommerceData,
    });
};

const formatOfferDetailsForGA = (offerDetails?: GA4Offer) => {
    if (offerDetails) {
        return {
            item_list_id: offerDetails.item_list_name,
            item_list_name: offerDetails.item_list_name,
            items: [offerDetails],
        };
    }
    return null;
};

export const handleOfferClick = async (
    e: FormEvent,
    bigQueryData: SanitisedOffer,
    index: number,
    offerPage: OfferPageTemplateResponse,
    site: Site,
    epcId: string,
    offerDetails?: GA4Offer,
    clickoutLocation?: ClickoutLocation
) => {
    e.preventDefault();

    if (isPrefetch()) {
        return;
    }

    const formTarget: HTMLFormElement = e.target as HTMLFormElement;
    buttonToggle(formTarget, true);

    const { fbp, fbc } = getFacebookCookies();

    if (site.attributes.googleTagManagerContainerId) {
        const hasAlreadyClickedOutThisSession = JSON.parse(
            getClientCookie(CLICK_OUT_COOKIE_NAME)
        );
        if (!hasAlreadyClickedOutThisSession) {
            /**
             * Unique should apply to the session in this instance.
             * So if a user ends the session and comes back again at a later date, they're able to
             * trigger unique clickout again.
             * But not more than once in a single session.
             * If a user comes to the page and click on 2 offers, this fires 3 events
             *  - 1 unique for the first offer.
             *  - 2 "normal" events ("total" in QiH's language), 1 event for each offer clicked on.
             */
            logNewGoogleAnalyticsEvent(
                "offer-click-out-unique",
                bigQueryData?.offerLink,
                undefined,
                epcId
            );

            // This expiry time is always going to be set as a date/time that is longer than the actual
            // session, but it doesn't matter as when the session is reset on the server this cookie is
            // also reset by the server.
            setCookie(
                CLICK_OUT_COOKIE_NAME,
                JSON.stringify(true),
                1000 * 60 * getGlobalConfig().sessionCookieMaxAgeMinutes
            );
        }

        const gaAttributes: GA4Offer = {
            ...offerDetails,
            item_list_name: getPageSlug(window.location.href),
        };

        logNewGoogleAnalyticsEvent(
            "offer-click-out",
            bigQueryData?.offerLink,
            gaAttributes,
            epcId
        );
    }

    const offerLink = bigQueryData?.offerLink;
    const merchantOrderArray = getMerchantOrderingFromOfferPage(offerPage);
    const offerPageTemplateName = offerPage?.data?.attributes?.name;

    const partialSite = {
        id: site.id,
        attributes: {
            name: site.attributes.name,
            shortCode: site.attributes.shortCode,
            cakeOfferId: site.attributes.cakeOfferId,
            host: site.attributes.host,
        },
    };

    // TODO: The beacon API can sometimes not be queued because it exceeds the
    //  maximum size of 64KB. If this happens, we send the data via a POST request instead.
    //  We should find a way to reduce the size of the data being sent, which could be
    //  done by removing unnecessary data from the object.

    //  UPDATE AND WARNING - 13/03/2024
    //  Do not send too much data in this request.
    //  An issue was discovered in ACD-16418 where we discovered that the facebook browser
    //  was unable to open new tabs. This meant that falling back to a fetch request failed
    //  After discussing with Andy and the team, it was agreed that we should remove
    //  cmsOtherOthers, cmsOfferPage and cmsSite from the EPC report.
    //  cmsOfferPage and cmsSite are already recorded in the visit report
    //  and cmsOtherOffers can be inferred from the partner_position and the cmsOfferPage
    const allData = {
        bigQueryData,
        index,
        href: window.location.href,
        epcId,
        merchantOrderArray,
        offerPageTemplateName,
        site: partialSite,
        offerLink,
        userAgent: navigator.userAgent,
        fbp: fbp,
        fbc: fbc,
        clickout_location: clickoutLocation,
    };

    // In ACD-16136 we discovered that window.fetch cannot work alongside window.open on iOS
    // and the order of these two functions were reversed (first open the tab, then send the data)
    // However, this is unreliable in the Facebook browser which cannot open new tabs, and instead replaces
    // the current tab.
    // To avoid this issue altogether, we have reduced the amount of data being sent so we can always use the beacon
    // API, and can put the sendBeaconOrFetch function back to its original order.
    await sendBeaconOrFetch("/api/offerSubmit", allData, "offerSubmit");

    window.open(offerLink, "_blank");
};

const getPageSlug = (url: string): string => {
    const urlParts = url.split("/");
    return urlParts[urlParts.length - 1];
};

export const OfferList = ({
    offerPage,
    site,
    initialViewportWidth,
}: {
    offerPage: OfferPageTemplateResponse;
    site: Site;
    initialViewportWidth: number;
}): ReactElement => {
    const mobileBreakpoint =
        offerPage?.data?.attributes?.OP?.mobileBreakpoint ?? 768;
    const { isMobileWidth } = useBreakpoint(
        mobileBreakpoint,
        initialViewportWidth
    );

    const desktopOfferPresentation = getMainOfferPresentation(offerPage);
    const mobileOfferPresentation = offerPage?.data?.attributes?.OP?.OPM?.data;

    const canUseMobilePresentation = getCanUseMobilePresentation(
        offerPage,
        mobileBreakpoint
    );

    const pageFeatures = offerPage?.data?.attributes?.PF;
    const offerListAttr = {
        offerPageId: offerPage.data?.id,
        offerListId:
            offerPage.data?.attributes?.offer_list?.data?.id || "page-specific",
        offerPresentationId:
            offerPage.data?.attributes?.offer_presentation?.data?.id,
    };

    const headerHTML = getSanitisedHeaderHTML(desktopOfferPresentation);
    const headerHTMLMobile = getSanitisedHeaderHTML(mobileOfferPresentation);

    const [chosenSanitisedOffer, chosenStyles, chosenHeaderHtml] =
        useMemo(() => {
            if (isMobileWidth && canUseMobilePresentation) {
                return [
                    offerPage.sanitisedOfferMobile || [],
                    offerPage.sanitisedStylesMobile,
                    headerHTMLMobile || "",
                ];
            }
            return [
                offerPage.sanitisedOffer || [],
                offerPage.sanitisedStyles,
                headerHTML || "",
            ];
        }, [
            mobileOfferPresentation,
            desktopOfferPresentation,
            isMobileWidth,
            canUseMobilePresentation,
            offerPage.sanitisedOfferMobile,
            offerPage.sanitisedOffer,
            offerPage.sanitisedStylesMobile,
            offerPage.sanitisedStyles,
            headerHTMLMobile,
            headerHTML,
        ]);

    useEffect(() => {
        const options = {
            rootMargin: "0px",
            threshold: 0.9,
        };
        // Keep track of which offers have been seen to prevent calling
        // the GA event multiple times with the same offer
        const offersSeen: Element[] = [];
        const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    if (!offersSeen.includes(entry.target)) {
                        offersSeen.push(entry.target);
                        const stringGAAttributes =
                            entry.target.getAttribute("data-ga-attributes") ??
                            "";

                        const jsonGAAttributes = JSON.parse(stringGAAttributes);
                        const gaAttributes: GA4Offer = {
                            ...jsonGAAttributes,
                            item_list_name: getPageSlug(window.location.href),
                        };

                        logNewOfferListViewEvent(gaAttributes);
                    }
                }
            });
        }, options);

        const offers = document.querySelectorAll(`.${offerRowClass}`);
        offers.forEach((offer) => {
            observer.observe(offer);
        });

        const adRows = document.querySelectorAll(`.${adRowClass}`);

        adRows.forEach((adRow) => {
            observer.observe(adRow);
        });
        return () => {
            observer.disconnect();
        };
    }, []);

    let perceivedIndex = 1;
    const renderBlockAndIncrementIndex = (reactContentToShow: ReactElement) => {
        // this will increment for offers and ad rows, but not title rows.
        perceivedIndex = perceivedIndex + 1;
        return reactContentToShow;
    };
    return (
        <section
            id="offers"
            className={styles.offers}
            data-attr={JSON.stringify(offerListAttr)}
        >
            {offerPage.data && chosenSanitisedOffer.length > 0 && (
                <div className={styles.offerContainer}>
                    {chosenHeaderHtml && (
                        <div
                            className={styles.offersHeading}
                            dangerouslySetInnerHTML={{
                                __html: chosenHeaderHtml,
                            }}
                        />
                    )}
                    {chosenSanitisedOffer?.map((offer, index) => (
                        <Fragment key={offer.id}>
                            {findAllPageFeaturesAtIndex(
                                pageFeatures,
                                index
                            )?.map((feature) => {
                                if (
                                    "ad_unit" in feature &&
                                    feature?.ad_unit?.data
                                ) {
                                    return renderBlockAndIncrementIndex(
                                        getAdRow(
                                            feature.ad_unit,
                                            offerPage,
                                            site,
                                            feature?.sanitisedAdUnit,
                                            perceivedIndex,
                                            initialViewportWidth
                                        )
                                    );
                                } else if (
                                    "title_unit" in feature &&
                                    feature?.title_unit
                                ) {
                                    return (
                                        <div
                                            key={perceivedIndex}
                                            className={
                                                styles.adOrTitleRowContainer
                                            }
                                        >
                                            <div
                                                className={
                                                    styles.adOrTitleRowImage
                                                }
                                            >
                                                <TitleUnit
                                                    titleUnit={
                                                        feature.title_unit
                                                    }
                                                />
                                            </div>
                                        </div>
                                    );
                                }
                            })}
                            {renderBlockAndIncrementIndex(
                                <OfferBlockWithForm
                                    key={offer.id}
                                    offer={offer}
                                    useMerchantColours={
                                        offerPage?.data?.attributes
                                            ?.useMerchantColours
                                    }
                                    index={perceivedIndex}
                                    offerPage={offerPage}
                                    site={site}
                                    epcId={offer.epcId ?? ""}
                                />
                            )}
                        </Fragment>
                    ))}
                </div>
            )}
            <style
                dangerouslySetInnerHTML={{
                    __html: chosenStyles || "",
                }}
            ></style>
        </section>
    );
};

const findAllPageFeaturesAtIndex = (
    pageFeatures: PageFeature[] | undefined,
    index: number
): PageFeature[] | undefined => {
    return pageFeatures?.filter((feature) => feature.position === index + 1);
};

const getAdRow = (
    adUnit: AdUnitResponse,
    offerPage: OfferPageTemplateResponse,
    site: Site,
    sanitisedOffer?: SanitisedOffer | null,
    perceivedIndex?: number,
    initialViewportWidth?: number
) => {
    return (
        <div key={perceivedIndex} className={styles.adOrTitleRowContainer}>
            <div className={styles.adOrTitleRowImage}>
                <AdUnit
                    offerPage={offerPage}
                    site={site}
                    altText={"Ad unit"}
                    responsive
                    sanitisedOffer={sanitisedOffer}
                    perceivedIndex={perceivedIndex}
                    adUnit={adUnit}
                    initialViewportWidth={initialViewportWidth}
                />
            </div>
        </div>
    );
};

const getCanUseMobilePresentation = (
    offerPage: OfferPageTemplateResponse,
    offerPresentationBreakpoint: number | null
): boolean => {
    return Boolean(
        offerPresentationBreakpoint &&
            offerPage.sanitisedOfferMobile?.length &&
            offerPage.sanitisedOfferMobile?.length > 0
    );
};

const getSanitisedHeaderHTML = (
    offerPresentation: OfferPresentation | undefined
): string => {
    return offerPresentation?.attributes?.sanitisedHeaderHTML ?? "";
};

export const getMainOfferPresentation = (
    offerPageTemplate: OfferPageTemplateResponse
): OfferPresentation | undefined => {
    // Offers will first return the desktop offers, then fallback to mobile, then to single presentation
    return (
        offerPageTemplate?.data?.attributes?.OP?.OPD?.data ||
        offerPageTemplate?.data?.attributes?.OP?.OPM?.data ||
        offerPageTemplate?.data?.attributes?.offer_presentation?.data
    );
};
